/**
 * Logic form ghi nhanh — thuần hàm, có test. Form giữ chuỗi số tiền như người
 * dùng gõ ("50k", "1.5tr"); chuyển thành số ở đây, và dựng đúng bản ghi mà bản cũ
 * vẫn đọc được (category = TÊN danh mục, status 'active' cho thu nhập…).
 */
import { z } from 'zod';
import { parseSmartMoney } from '../../../utils/currency';
import { effectOf, checkChange, type SourceState } from '../../../ledger/effects';

export type EntryFormKind = 'expense' | 'income' | 'refund';

export const parseAmount = (text: string): number => {
  const n = parseSmartMoney(text || '');
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
};

export const entryFormSchema = z.object({
  kind: z.enum(['expense', 'income', 'refund']),
  amountText: z.string(),
  category: z.string().min(1, 'Chọn danh mục'),
  walletId: z.string().min(1, 'Chọn ví'),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Chọn ngày'),
  description: z.string().max(500, 'Ghi chú tối đa 500 ký tự').optional(),
  /** Chỉ cho thu nhập: tiền vay giải ngân — không tính là thu nhập */
  isLoanProceeds: z.boolean().optional()
}).superRefine((v, ctx) => {
  if (parseAmount(v.amountText) <= 0) {
    ctx.addIssue({ code: 'custom', path: ['amountText'], message: 'Nhập số tiền lớn hơn 0 (vd: 50k, 1.5tr, 2 tỷ)' });
  }
});
export type EntryFormValues = z.infer<typeof entryFormSchema>;

/** Resolver react-hook-form dựa trên Zod — bỏ phụ thuộc @hookform/resolvers. */
export const zodResolver = (schema: z.ZodType<any>) => async (values: any) => {
  const r = schema.safeParse(values);
  if (r.success) return { values: r.data, errors: {} };
  const errors: Record<string, { type: string; message: string }> = {};
  for (const issue of r.error.issues) {
    const key = String(issue.path[0] ?? 'root');
    if (!errors[key]) errors[key] = { type: issue.code, message: issue.message };
  }
  return { values: {}, errors };
};

export interface CategoryOption { name: string; icon?: string; color?: string }
export interface WalletOption { id: string; name: string }

/** Bản ghi gửi cho ledger. Thu → incomes; chi & hoàn tiền → expenses. */
export const toPayload = (
  v: EntryFormValues, categories: CategoryOption[], wallets: WalletOption[]
): { collection: 'incomes' | 'expenses'; data: Record<string, any> } => {
  const cat = categories.find(c => c.name === v.category);
  const wallet = wallets.find(w => w.id === v.walletId);
  const base: Record<string, any> = {
    amount: parseAmount(v.amountText),
    category: v.category,
    categoryName: v.category,
    walletId: v.walletId,
    walletName: wallet?.name ?? '',
    description: v.description?.trim() || '',
    transactionDate: v.date
  };
  if (cat?.icon) base.categoryIcon = cat.icon;
  if (cat?.color) base.categoryColor = cat.color;
  return v.kind === 'income'
    ? { collection: 'incomes', data: { ...base, status: 'active', isLoanProceeds: !!v.isLoanProceeds } }
    : { collection: 'expenses', data: { ...base, isRefund: v.kind === 'refund' } };
};

/**
 * Báo trước (khi gõ) nếu khoản mới sẽ bị chặn/cảnh báo — dùng CHÍNH chính sách của
 * sổ cái, để người dùng thấy lý do ngay trên form thay vì sau khi bấm Lưu.
 */
export const previewPolicy = (
  kind: EntryFormKind,
  amount: number,
  wallet: (SourceState & { id: string }) | undefined
): { level: 'ok' | 'warn' | 'block'; message?: string } => {
  if (!wallet || amount <= 0) return { level: 'ok' };
  const e = effectOf(kind === 'income' ? 'income' : 'expense', { amount, isRefund: kind === 'refund', walletId: wallet.id }, () => wallet.type === 'credit');
  if (!e) return { level: 'ok' };
  const r = checkChange(wallet, e.field, e.delta);
  if (!r.ok) return { level: 'block', message: r.error };
  return r.warning ? { level: 'warn', message: r.warning } : { level: 'ok' };
};

/** Số tiền cho phép 0 (vd. đối soát về 0): null khi để trống/không hợp lệ. */
export const parseMoneyOrNull = (text: string): number | null => {
  if (!text?.trim()) return null;
  const n = parseSmartMoney(text);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
};

/** Ngày (yyyy-mm-dd) sau hôm nay? Hoàn tiền và sửa giao dịch cũ không bị chặn — xem EntrySheet. */
export const isFutureDate = (isoDate: string, now: Date = new Date()): boolean => {
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return /^\d{4}-\d{2}-\d{2}$/.test(isoDate) && isoDate > today;
};
